import React from "react";
import { Pressable, StyleSheet, View, useWindowDimensions } from "react-native";
import { Title13, Title18 } from "@stylesheets";
import { Colors } from "@constants";
import { I18n } from "react-redux-i18n";
import { Delete, Modify } from "@assets";
import { useDispatch, useSelector } from "react-redux";
import { GlobalState } from "@redux";
import { HoldingView, UserProfile } from "@foodi/core";
import { TestIDs } from "@utils";
import { useDevices } from "@hooks";
import { HoldingViewModel } from "@modules";
import { NavigationProp } from "@react-navigation/native";
import { SecurityCode } from "@assets/icons/SecurityCode";
import DisplaySecurityCodeModal from "../DisplaySecurityCodeModal";

interface IProps {
  navigation: NavigationProp<any>;
  hv: HoldingView;
  index: number;
  handleModifyHoldingView: (hv: HoldingView) => () => void;
  handleHoldingViewSwitch: (hv: HoldingView) => () => void;
  handleDeleteHoldingView: (
    hv: HoldingView,
    index: number,
    isActiveHoldingView?: boolean
  ) => () => void;
}
export const HoldingViewRow: React.FC<IProps> = React.memo(
    ({
         hv,
         index,
         handleModifyHoldingView,
         handleDeleteHoldingView,
         handleHoldingViewSwitch,
     }) => {
        const dispatch = useDispatch();
        const [isMobile] = useDevices();
        const { width } = useWindowDimensions();
        const isCompact = isMobile || width <= 700;
        const styles = _styles(isCompact);
        const [isHover, setIsHover] = React.useState<string>();

    const [openShowSecurityCodeModal, setOpenShowSecurityCodeModal] = React.useState<boolean>(false);

    const HoldingViewRowText: React.FC<any> = isCompact ? Title13 : Title18;
    const isScholapassSystem: boolean = hv?.holding?.isScolaPassCashSystem === true;

    const hasBadge: boolean = hv?.guest !== null && (hv?.guest?.serialNumber !== '' || hv?.guest?.serialNumber !== null);

    const validationCode: string = hv?.guest?.validationCode?.toString() || '';

    const { idHoldingView } =
      useSelector((state: GlobalState) => state.auth?.userInfo) ??
      ({} as UserProfile);

    const holdingViewModel = new HoldingViewModel(dispatch, isMobile);

    const setHoverValue = (value?: string) =>
      /* istanbul ignore next */
      () => setIsHover(value);

    const modifyIconSize = isCompact ? 24 : 30;
    const securityCodeIconSize = isCompact ? 24 : 30;

    const isActiveHoldingView = holdingViewModel.isActiveHoldingView(
      idHoldingView,
      hv.id
    );
    const rowStyle = isActiveHoldingView
      ? [styles.holdingView, styles.activeHoldingView]
      : [styles.holdingView];

    const { firstName } =
      useSelector((state: GlobalState) => state.auth?.userInfo) ??
      ({} as UserProfile);

    const { alias, establishment, badge } = holdingViewModel.getHoldingViewRowData(hv, firstName);

        const renderActions = () => {
            const compactButtonStyle = [
                styles.actionButtonCompact,
                isActiveHoldingView && styles.actionButtonActive
            ];

            return (
                <View style={styles.actions}>
                    {hasBadge && (<Pressable
                        testID={TestIDs.profile.actions.pressShowSecurityCode}
                        style={isCompact ? compactButtonStyle : styles.marginLeft}
                        onPress={() => setOpenShowSecurityCodeModal(true)}
                    >
                        <SecurityCode width={securityCodeIconSize} height={securityCodeIconSize}/>
                    </Pressable>)}
                    <Pressable
                        testID={TestIDs.profile.actions.pressEditHoldingView}
                        style={isCompact ? compactButtonStyle : styles.marginLeft}
                        onPress={handleModifyHoldingView(hv)}
                    >
                        <Modify width={modifyIconSize} height={modifyIconSize}/>
                    </Pressable>
                    <Pressable
                        testID={TestIDs.profile.actions.pressDeleteHoldingView}
                        style={isCompact ? compactButtonStyle : styles.separator}
                        onPress={handleDeleteHoldingView(hv, index + 1, isActiveHoldingView)}
                    >
                        <Delete/>
                    </Pressable>
                </View>
            );
        };

        if (isCompact) {
            return (
                <View>
                    {isActiveHoldingView && (
                        <HoldingViewRowText style={styles.active} color={Colors.middleGreen}>
                            {I18n.t("account.active")}
                        </HoldingViewRowText>
                    )}
                    <View style={[styles.mobileWrapper, isActiveHoldingView && styles.activeHoldingView, styles.marginBottom]}>
                        <Pressable
                            key={hv.id}
                            style={styles.mobileInfoSection}
                            disabled={isActiveHoldingView}
                            onPress={handleHoldingViewSwitch(hv)}
                        >
                            <HoldingViewRowText
                                bold isBold
                                color={Colors.foodiBlack}
                                style={isActiveHoldingView && styles.activeHoldingViewText}
                                numberOfLines={1}
                                ellipsizeMode='tail'
                            >
                                {alias}
                            </HoldingViewRowText>
                            <HoldingViewRowText
                                bold isBold
                                color={Colors.foodiBlack}
                                style={[styles.mobileInfoText, isActiveHoldingView && styles.activeHoldingViewText]}
                                numberOfLines={2}
                                ellipsizeMode='tail'
                            >
                                {establishment}
                            </HoldingViewRowText>
                            <HoldingViewRowText
                                bold isBold
                                color={Colors.foodiBlack}
                                style={[styles.mobileInfoText, isActiveHoldingView && styles.activeHoldingViewText]}
                            >
                                {badge}
                            </HoldingViewRowText>
                        </Pressable>
                        {renderActions()}
                    </View>
                    {index === 0 && <View style={styles.divider}/>}
                    {openShowSecurityCodeModal && (
                        <DisplaySecurityCodeModal
                            securityCode={validationCode}
                            isScholapass={isScholapassSystem}
                            closeModal={() => setOpenShowSecurityCodeModal(false)}
                        />
                    )}
                </View>
            );
        }

        return (
            <View>
                {isActiveHoldingView && (
                    <HoldingViewRowText style={styles.active} color={Colors.middleGreen}>
                        {I18n.t("account.active")}
                    </HoldingViewRowText>
                )}
                <View style={[styles.row, styles.marginBottom]}>
                    <Pressable
                        key={hv.id}
                        style={[...rowStyle, isHover === hv.id && styles.rowHover]}
                        //@ts-ignore
                        onHoverIn={setHoverValue(hv.id)}
                        onHoverOut={setHoverValue(undefined)}
                        disabled={isActiveHoldingView}
                        onPress={handleHoldingViewSwitch(hv)}
                    >
                        <View style={styles.aliasContainer}>
                            <HoldingViewRowText
                                bold isBold
                                color={Colors.foodiBlack}
                                style={isActiveHoldingView && styles.activeHoldingViewText}
                                numberOfLines={1}
                                ellipsizeMode='tail'
                            >
                                {alias}
                            </HoldingViewRowText>
                        </View>
                        <View style={styles.ellipsisText}>
                            <HoldingViewRowText
                                bold isBold
                                color={Colors.foodiBlack}
                                style={isActiveHoldingView && styles.activeHoldingViewText}
                                numberOfLines={1}
                                ellipsizeMode='tail'
                            >
                                {establishment}
                            </HoldingViewRowText>
                        </View>
                        <View>
                            <HoldingViewRowText
                                bold isBold
                                color={Colors.foodiBlack}
                                style={isActiveHoldingView && styles.activeHoldingViewText}
                            >
                                {badge}
                            </HoldingViewRowText>
                        </View>
                    </Pressable>
                    {renderActions()}
                </View>
                {index === 0 && <View style={styles.divider}/>}
                {openShowSecurityCodeModal && (
                    <DisplaySecurityCodeModal
                        securityCode={validationCode}
                        isScholapass={isScholapassSystem}
                        closeModal={() => setOpenShowSecurityCodeModal(false)}
                    />
                )}
            </View>
        );
    }
);

const _styles = (isCompact: boolean) =>  StyleSheet.create({
    holdingView: {
        backgroundColor: Colors.disabledBackground,
        borderRadius: 15,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderWidth: 2,
        borderColor: Colors.transparent,
        flexDirection: "row",
        flexShrink: 1,
        flexGrow: 0,
    },
    mobileWrapper: {
        backgroundColor: Colors.white,
        borderRadius: 15,
        paddingVertical: 14,
        paddingHorizontal: 16,
        borderWidth: 1,
        borderColor: "#E4E6EA",
        shadowColor: "#000",
        shadowOpacity: 0.05,
        shadowRadius: 6,
        shadowOffset: { width: 0, height: 3 },
        elevation: 2,
    },
    mobileInfoSection: {
        flex: 1,
    },
    mobileInfoText: {
        marginTop: 4,
        color: Colors.foodiBlack,
    },
    activeHoldingView: {
        backgroundColor: Colors.middleGreen,
    },
    activeHoldingViewText: {
        color: Colors.white,
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
    },
    separator: {
        marginLeft: 10,
    },
    active: {
        fontWeight: "bold",
        fontSize: 13
    },
    divider: {
        height: 1,
        backgroundColor: '#DDDDDD',
        ...(isCompact ? { marginBottom: 25 } : { marginBottom: 15 }),
    },
    ellipsisText: {
        flexShrink: 5,
        flexGrow: 0
    },
    marginLeft: {
        marginLeft: 20,
    },
    rowHover: {
        borderColor: Colors.middleGreen,
        borderWidth: 2,
    },
    marginBottom: {
        ...(isCompact ? { marginBottom: 20 } : { marginBottom: 15 }),
    },
    actions: {
        flexDirection: "row",
        alignItems: "center",
        ...(isCompact ? {
            marginTop: 12,
            justifyContent: "flex-end",
            flexWrap: "wrap",
            columnGap: 10,
            rowGap: 8,
        } : {
            marginLeft: 10,
        }),
    },
    actionButtonCompact: {
        padding: 8,
        borderRadius: 22,
        backgroundColor: Colors.white,
        borderWidth: 1,
        borderColor: "#D8DBE2",
        shadowColor: "#000",
        shadowOpacity: 0.04,
        shadowRadius: 3,
        shadowOffset: { width: 0, height: 1 },
        elevation: 1,
        minWidth: 36,
        alignItems: "center",
        justifyContent: "center",
    },
    actionButtonActive: {
        backgroundColor: Colors.white,
        borderColor: Colors.middleGreen,
        shadowOpacity: 0.08,
        shadowRadius: 4,
        shadowOffset: { width: 0, height: 2 },
    },
    actionButton: {
        marginLeft: 15,
    },
    aliasContainer: {
        flexShrink: 1,
        flexGrow: 0,
        minWidth: 50
    }
});
